// A question as students see it: the case scenario, the question, the answer controls; and after
// submitting, their answer next to the correct one with the explanation.
import { useState } from 'preact/hooks';
import { Icon } from './icons.js';
import { Chip } from './components.jsx';
import { Linked, Rich } from './rich.jsx';
import { AnswerPhotoGrid, AnswerPhotoPicker } from './answer-photos.jsx';

export const LETTERS = 'ABCDEFGH';
export const TYPE_LABEL = {
  mcq: 'Multiple choice', mr: 'Multiple response', number: 'Number', dropdown: 'Drop-down', written: 'Written',
};

export function marksLabel(m) {
  const n = Number(m);
  return `${Number.isInteger(n) ? n : n.toFixed(1)} mark${n === 1 ? '' : 's'}`;
}

export function scoreText(score, of) {
  const f = (v) => { const n = Number(v); return Number.isInteger(n) ? String(n) : n.toFixed(1); };
  return `${f(score)} / ${f(of)}`;
}

export function percent(score, of) {
  return Number(of) > 0 ? Math.round((Number(score) * 100) / Number(of)) : 0;
}

// Indian digit grouping for a number answer that is shown back ("12,50,000" stays as typed; this formats ours).
export function numberText(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 6 });
}

export function Scenario({ q, open: startOpen = true }) {
  const [open, setOpen] = useState(startOpen);
  if (!q.case_id || !q.scenario) return null;
  return (
    <section class={`scenario${open ? '' : ' is-closed'}`} aria-label={`Case: ${q.case_title}`}>
      <button type="button" class="scenario-head" aria-expanded={open ? 'true' : 'false'} onClick={() => setOpen(!open)}>
        <Icon name="book" /><span>Case: {q.case_title}</span><Icon name={open ? 'up' : 'down'} />
      </button>
      {open ? <Rich text={q.scenario} class="scenario-text" /> : null}
    </section>
  );
}

export function wordCount(text) {
  const t = String(text || '').trim();
  return t ? t.split(/\s+/).length : 0;
}

// A written answer with its photos (each once, five at most), or null when it has neither.
function written(base, patch) {
  const next = { ...(base || {}), ...patch };
  if (next.photos) next.photos = [...new Set(next.photos)].slice(0, 5);
  if (!next.photos || !next.photos.length) delete next.photos;
  return (next.text && next.text.trim()) || next.photos ? next : null;
}

/* ---------------------------------------------------------------- answering */
// onChange gets the new answer, or for written answers a function of the current one (so a photo that
// finishes uploading never undoes typing done meanwhile). allowPhotos: mocks take photos of workings.
export function AnswerInput({ q, value, onChange, idBase, disabled, allowPhotos, uploading, onUploading }) {
  const r = value || {};
  if (q.type === 'mcq' || q.type === 'mr') {
    const multi = q.type === 'mr';
    const chosen = multi ? new Set(r.choices || []) : new Set(r.choice === undefined || r.choice === null ? [] : [r.choice]);
    const toggle = (i) => {
      if (!multi) { onChange({ choice: i }); return; }
      const next = new Set(chosen);
      if (next.has(i)) next.delete(i); else next.add(i);
      onChange(next.size ? { choices: [...next].sort((a, b) => a - b) } : null);
    };
    return (
      <fieldset class="choices" disabled={disabled}>
        <legend class={multi ? 'choices-legend' : 'sr-only'}>{multi ? `Choose ${q.pick || 'all that apply'}` : 'Choose one answer'}</legend>
        {q.options.map((o, i) => (
          <label class={`choice${chosen.has(i) ? ' is-on' : ''}`} key={i}>
            <input type={multi ? 'checkbox' : 'radio'} name={`${idBase}-choice`} checked={chosen.has(i)} onChange={() => toggle(i)} />
            <span class="choice-letter" aria-hidden="true">{LETTERS[i]}</span>
            <span class="choice-text"><Linked text={o} /></span>
          </label>
        ))}
      </fieldset>
    );
  }
  if (q.type === 'number') {
    const o = q.options || {};
    return (
      <div class="field">
        <label for={`${idBase}-num`}>Your answer</label>
        <div class="num-input">
          {o.prefix ? <span class="num-affix" aria-hidden="true">{o.prefix}</span> : null}
          {/* The full keyboard: the iPhone number pad has no minus sign or brackets for a negative answer. */}
          <input id={`${idBase}-num`} type="text" inputMode="text" autocomplete="off" autocorrect="off" spellcheck={false} maxLength={40} disabled={disabled}
            value={r.text || ''} onInput={(e) => onChange(e.currentTarget.value.trim() ? { text: e.currentTarget.value } : null)}
            aria-describedby={`${idBase}-num-hint`} />
          {o.suffix ? <span class="num-affix" aria-hidden="true">{o.suffix}</span> : null}
        </div>
        <p class="field-hint" id={`${idBase}-num-hint`}>Numbers only{o.prefix || o.suffix ? ` (${[o.prefix, o.suffix].filter(Boolean).join(' … ')} is already there)` : ''}. Commas are fine; show a minus as -1,200 or (1,200).</p>
      </div>
    );
  }
  if (q.type === 'dropdown') {
    const choices = (r.choices || q.options.rows.map(() => null));
    const set = (row, v) => {
      const next = q.options.rows.map((_, k) => (k === row ? (v === '' ? null : Number(v)) : (choices[k] ?? null)));
      onChange(next.some((x) => x !== null) ? { choices: next } : null);
    };
    return (
      <div class="dropdowns">
        {q.options.rows.map((row, k) => (
          <div class="dropdown-row" key={k}>
            <label for={`${idBase}-dd-${k}`}><Linked text={row} /></label>
            <select id={`${idBase}-dd-${k}`} disabled={disabled} value={choices[k] ?? ''} onChange={(e) => set(k, e.currentTarget.value)}>
              <option value="">Choose…</option>
              {q.options.choices.map((c, i) => <option value={i} key={i}>{c}</option>)}
            </select>
          </div>
        ))}
      </div>
    );
  }
  // written
  const words = wordCount(r.text);
  return (
    <div class="field">
      <label for={`${idBase}-text`}>Your answer</label>
      <textarea id={`${idBase}-text`} rows={10} maxLength={20000} disabled={disabled} value={r.text || ''}
        onInput={(e) => { const text = e.currentTarget.value; onChange((cur) => written(cur, { text })); }}
        aria-describedby={`${idBase}-text-hint`} />
      <p class="field-hint" id={`${idBase}-text-hint`}>
        <span class="word-count">{words === 1 ? '1 word' : `${words} words`}</span>
        {' · '}Write it as you would in the exam. A table pasted from Excel keeps its columns.
      </p>
      {allowPhotos ? (
        <AnswerPhotoPicker paths={r.photos || []} disabled={disabled} id={`${idBase}-photos`} uploading={uploading} onUploading={onUploading}
          onAdd={(path) => onChange((cur) => written(cur, { photos: [...((cur && cur.photos) || []), path] }))}
          onRemove={(path) => onChange((cur) => written(cur, { photos: ((cur && cur.photos) || []).filter((x) => x !== path) }))} />
      ) : null}
    </div>
  );
}

export function answered(q, r) {
  if (!r) return false;
  if (q.type === 'mcq') return r.choice !== undefined && r.choice !== null;
  if (q.type === 'mr') return Boolean(r.choices && r.choices.length);
  if (q.type === 'dropdown') return Boolean(r.choices && r.choices.some((x) => x !== null && x !== undefined));
  return Boolean((r.text && r.text.trim()) || (r.photos && r.photos.length));
}

/* ---------------------------------------------------------------- after submitting */
function Mark({ ok, children }) {
  return <span class={`mark-tag ${ok ? 'is-ok' : 'is-bad'}`}><Icon name={ok ? 'done' : 'wrong'} />{children}</span>;
}

export function AnswerReview({ item, revealed, preview, whose }) {
  const { q } = item;
  const r = item.response || {};
  const has = answered(q, r);
  if (q.type === 'mcq' || q.type === 'mr') {
    const mine = new Set(q.type === 'mr' ? r.choices || [] : r.choice === undefined || r.choice === null ? [] : [r.choice]);
    const right = new Set(revealed ? (q.type === 'mr' ? q.answer : [q.answer]) : []);
    return (
      <ul class="review-choices">
        {q.options.map((o, i) => {
          const isMine = mine.has(i);
          const isRight = right.has(i);
          return (
            <li key={i} class={`${isRight ? 'is-right' : ''}${isMine && revealed && !isRight ? ' is-wrong' : ''}${isMine ? ' is-mine' : ''}`}>
              <span class="choice-letter" aria-hidden="true">{LETTERS[i]}</span>
              <span class="choice-text"><Linked text={o} /></span>
              <span class="review-tags">
                {isMine ? <Chip tone={revealed ? (isRight ? 'ok' : 'bad') : 'navy'}>Your answer</Chip> : null}
                {isRight ? <Chip tone="ok">Correct answer</Chip> : null}
              </span>
            </li>
          );
        })}
        {!has && !preview ? <li class="review-none">You didn’t answer this one.</li> : null}
      </ul>
    );
  }
  if (q.type === 'number') {
    const o = q.options || {};
    const ok = item.score !== null && item.score !== undefined && Number(item.score) > 0;
    return (
      <dl class="review-pairs">
        <div><dt>Your answer</dt><dd>{has ? <>{o.prefix}{r.text}{o.suffix}</> : 'Not answered'}</dd></div>
        {revealed ? (
          <div><dt>Correct answer</dt><dd>{o.prefix}{numberText(q.answer.value)}{o.suffix}
            {Number(q.answer.tolerance) > 0 ? <small> (± {numberText(q.answer.tolerance)} accepted)</small> : null}
            {' '}{has ? <Mark ok={ok}>{ok ? 'Right' : 'Not right'}</Mark> : null}</dd></div>
        ) : null}
      </dl>
    );
  }
  if (q.type === 'dropdown') {
    const mine = r.choices || [];
    return (
      <table class="review-table">
        <thead><tr><th scope="col">Statement</th><th scope="col">Your answer</th>{revealed ? <th scope="col">Correct answer</th> : null}</tr></thead>
        <tbody>
          {q.options.rows.map((row, k) => {
            const m = mine[k];
            const ok = revealed && m === q.answer[k];
            return (
              <tr key={k}>
                <td><Linked text={row} /></td>
                <td class={revealed && m !== null && m !== undefined ? (ok ? 'is-right' : 'is-wrong') : ''}>{m === null || m === undefined ? '—' : q.options.choices[m]}</td>
                {revealed ? <td>{q.options.choices[q.answer[k]]}</td> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  const photos = item.photos || [];
  return (
    <div class="review-written">
      <h4>{whose || 'Your answer'}</h4>
      {r.text && r.text.trim() ? <Rich text={r.text} /> : null}
      {photos.length ? <AnswerPhotoGrid paths={photos} /> : null}
      {!has && !photos.length ? <p class="muted">Not answered.</p> : null}
      {r.text && r.text.trim() ? <p class="field-hint">{wordCount(r.text)} words</p> : null}
    </div>
  );
}

// mock: written answers are marked by the teacher (marks and a comment) rather than self-checked.
export function QuestionReview({ item, revealed, number, preview, mock }) {
  const { q } = item;
  const scored = item.score !== null && item.score !== undefined;
  const full = scored && Number(item.score) >= Number(q.marks);
  let chip;
  if (q.type === 'written' && mock) chip = scored ? <Chip tone="navy">{Number(item.score)} / {marksLabel(q.marks)}</Chip> : <Chip>{marksLabel(q.marks)}</Chip>;
  else if (q.type === 'written') chip = <Chip>Check it yourself</Chip>;
  else if (scored) chip = <Chip tone={full ? 'ok' : 'bad'}>{full ? 'Right' : 'Not right'} · {Number(item.score)} / {Number(q.marks)}</Chip>;
  else chip = <Chip>{marksLabel(q.marks)}</Chip>;
  return (
    <article class="review-q" aria-labelledby={`rq-${item.id}`}>
      <header class="review-q-head">
        <h3 id={`rq-${item.id}`}>Question {number}</h3>
        <span class="review-q-meta">{chip}</span>
      </header>
      <Scenario q={q} open={false} />
      <Rich text={q.body} class="q-body" />
      <AnswerReview item={item} revealed={revealed} preview={preview} />
      {item.comment ? (
        <div class="teacher-comment">
          <h4>Teacher’s comment</h4>
          <Rich text={item.comment} />
        </div>
      ) : null}
      {revealed && q.explanation ? (
        <div class="explanation">
          <h4>{q.type === 'written' ? 'Model answer' : 'Explanation'}</h4>
          <Rich text={q.explanation} />
        </div>
      ) : null}
    </article>
  );
}
